import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export const usePaged = (rows, initialSize = 10) => {
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(initialSize);
  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  React.useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);
  const start = (page - 1) * pageSize;
  const pageRows = rows.slice(start, start + pageSize);
  return { page, setPage, pageSize, setPageSize, totalPages, pageRows, total, start };
};

export const PageSize = ({ value, onChange }) => (
  <div className="flex items-center gap-1" data-testid="page-size">
    {[10, 25, 50, 100].map((s) => (
      <button
        key={s}
        data-testid={`page-size-${s}`}
        onClick={() => onChange(s)}
        className={`h-8 w-9 rounded-md text-xs font-semibold transition-colors ${
          value === s ? "bg-primary text-white" : "text-muted-foreground hover:bg-accent"
        }`}
      >
        {s}
      </button>
    ))}
  </div>
);

export const Pager = ({ page, totalPages, setPage }) => {
  if (totalPages <= 1) return null;
  const nums = [];
  const add = (n) => nums.push(n);
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) add(i);
  } else {
    add(1);
    if (page > 3) add("…");
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) add(i);
    if (page < totalPages - 2) add("…");
    add(totalPages);
  }
  return (
    <div className="flex items-center justify-center gap-1 py-4" data-testid="pager">
      <button onClick={() => setPage(Math.max(1, page - 1))} className="h-8 w-8 rounded-md hover:bg-accent inline-flex items-center justify-center text-muted-foreground" data-testid="pager-prev">
        <ChevronLeft className="h-4 w-4" />
      </button>
      {nums.map((n, i) =>
        n === "…" ? (
          <span key={i} className="px-2 text-muted-foreground">…</span>
        ) : (
          <button
            key={i}
            data-testid={`pager-page-${n}`}
            onClick={() => setPage(n)}
            className={`h-8 min-w-8 px-2 rounded-md text-sm font-medium transition-colors ${
              page === n ? "border border-primary text-primary" : "text-muted-foreground hover:bg-accent"
            }`}
          >
            {n}
          </button>
        )
      )}
      <button onClick={() => setPage(Math.min(totalPages, page + 1))} className="h-8 w-8 rounded-md hover:bg-accent inline-flex items-center justify-center text-muted-foreground" data-testid="pager-next">
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
};

export const ModuleTabs = ({ tabs, active, onChange }) => (
  <div className="flex items-center gap-6 border-b border-border mb-5 overflow-x-auto" data-testid="module-tabs">
    {tabs.map((t) => (
      <button
        key={t.id}
        data-testid={`tab-${t.id}`}
        onClick={() => onChange(t.id)}
        className={`pb-3 -mb-px text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
          active === t.id ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
        }`}
      >
        {t.label}
      </button>
    ))}
  </div>
);
